import { createContext, useContext, ReactNode, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { convertCurrency } from '@/lib/currencyConversion';
import { useAuth } from './AuthContext';
import { localDatabase } from '@/services/localDatabase';

interface BudgetData {
  monthlyBudget: number;
  spent: number;
  remaining: number;
  currency: string;
  dailyBudget: number;
  daysLeft: number;
  percentUsed: number;
  isOverBudget: boolean;
  isOnTrack: boolean;
}

interface BudgetContextType {
  data: BudgetData | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
}

const defaultBudgetData: BudgetData = {
  monthlyBudget: 1000,
  spent: 0,
  remaining: 1000,
  currency: 'EUR',
  dailyBudget: 0,
  daysLeft: 30,
  percentUsed: 0,
  isOverBudget: false,
  isOnTrack: true,
};

const BudgetContext = createContext<BudgetContextType | undefined>(undefined);

function calculateBudgetResult(
  monthlyBudget: number,
  spent: number,
  currency: string,
  now: Date,
  lastDay: Date
): BudgetData {
  const remaining = monthlyBudget - spent;
  const percentUsed = (spent / monthlyBudget) * 100;
  const isOverBudget = remaining < 0;
  const daysLeft = Math.max(1, Math.ceil((lastDay.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  const dailyBudget = remaining > 0 ? remaining / daysLeft : 0;
  return { monthlyBudget, spent, remaining, currency, dailyBudget, daysLeft, percentUsed, isOverBudget, isOnTrack: !isOverBudget };
}

async function fetchBudgetData(userId: string): Promise<BudgetData> {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  // Use local YYYY-MM-DD (toISOString shifts to UTC and breaks month boundaries near midnight in non-UTC timezones)
  const toLocalYMD = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const firstDayStr = toLocalYMD(firstDay);

  // Try cached budget settings first for instant display
  let monthlyBudget = 1000;
  let currency = 'EUR';
  const cached = await localDatabase.getCachedBudgetSettings(userId);
  if (cached) {
    monthlyBudget = cached.monthly_budget;
    currency = cached.currency;
  }

  // Fetch budget settings from server (small, rarely changes)
  if (navigator.onLine) {
    const budgetResult = await supabase
      .from('budget_settings')
      .select('monthly_budget, currency')
      .eq('user_id', userId)
      .maybeSingle();

    if (budgetResult.data) {
      monthlyBudget = budgetResult.data.monthly_budget ?? monthlyBudget;
      currency = budgetResult.data.currency ?? currency;
      // Update cache
      localDatabase.setCachedBudgetSettings({
        user_id: userId,
        monthly_budget: monthlyBudget,
        currency,
        updated_at: new Date().toISOString(),
      });
    }
  }

  // Read local expenses first so newly-added or unsynced items count immediately
  const lastDayStr = toLocalYMD(lastDay);
  const localExpenses = await localDatabase.getExpenses(userId, firstDayStr, lastDayStr);

  // Online: merge server + unsynced local expenses
  if (navigator.onLine) {
    const { data: serverExpenses } = await supabase
      .from('expenses')
      .select('id, amount, amount_eur, currency, expense_date, merchant')
      .eq('user_id', userId)
      .gte('expense_date', firstDayStr)
      .lte('expense_date', lastDayStr);

    if (serverExpenses) {
      const serverIds = new Set(serverExpenses.map((exp) => exp.id));
      const serverKeys = new Set(
        serverExpenses.map((e: any) => `${e.expense_date}|${Number(e.amount).toFixed(2)}|${(e.merchant || '').trim().toLowerCase()}`)
      );
      const mergedExpenses: Array<{ id: string; amount: number; currency: string; amount_eur?: number | null }> = [
        ...serverExpenses,
        ...localExpenses.filter((exp) => {
          if (serverIds.has(exp.id)) return false;
          const key = `${exp.expense_date}|${Number(exp.amount).toFixed(2)}|${(exp.merchant || '').trim().toLowerCase()}`;
          if (serverKeys.has(key)) return false;
          return true;
        }).map((exp) => ({
          id: exp.id,
          amount: exp.amount,
          currency: exp.currency,
          amount_eur: exp.amount_eur ?? null,
        })),
      ];

      const spent = mergedExpenses.reduce((sum, exp) => {
        // Prefer the EUR-equivalent stamped at write time. Fall back to live conversion only if missing (legacy rows).
        const eur = exp.amount_eur != null
          ? Number(exp.amount_eur)
          : convertCurrency(Number(exp.amount), exp.currency || 'EUR', 'EUR');
        return sum + convertCurrency(eur, 'EUR', currency);
      }, 0);

      // Self-heal Dexie in the background.
      try {
        await localDatabase.purgeDuplicateExpenses(userId, serverExpenses as any);
      } catch {/* non-blocking */}

      return calculateBudgetResult(monthlyBudget, spent, currency, now, lastDay);
    }
  }

  // Offline fallback: use IndexedDB only
  const spent = localExpenses.reduce((sum, exp) => {
    const eur = exp.amount_eur != null
      ? Number(exp.amount_eur)
      : convertCurrency(Number(exp.amount), exp.currency || 'EUR', 'EUR');
    return sum + convertCurrency(eur, 'EUR', currency);
  }, 0);
  return calculateBudgetResult(monthlyBudget, spent, currency, now, lastDay);
}

export function BudgetProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['budget', user?.id],
    queryFn: () => fetchBudgetData(user!.id),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
  });

  const refresh = useCallback(() => {
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ['budget', user.id] });
    }
  }, [user?.id, queryClient]);

  useEffect(() => {
    const handleReconciled = () => refresh();
    window.addEventListener('wren:data-reconciled', handleReconciled);
    return () => window.removeEventListener('wren:data-reconciled', handleReconciled);
  }, [refresh]);

  return (
    <BudgetContext.Provider value={{ 
      data: data ?? null, 
      isLoading, 
      error: error as Error | null,
      refresh 
    }}>
      {children}
    </BudgetContext.Provider>
  );
}

export function useBudget() {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within BudgetProvider');
  }
  return context;
}
